import React, { useEffect, useRef, useState } from "react";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Box } from "@mui/system";
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOffOutlinedIcon from "@mui/icons-material/LocationOffOutlined";
import authAxios from "setups/axios";
import { toastMessage } from "utils/toastMessage";
import { Toaster } from "react-hot-toast";
import moment from "moment";
import { updateLocation } from "_controllers/officer";
import haversine from "haversine-distance";
import { PageHeader, SectionCard, EmptyState, TablePager } from "_shared/ui";
import "./styles.css";

const Main = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState("");
  const [long, setLong] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const searchTimeout = useRef(null);

  const messageStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50%",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 2,
    textAlign: "center",
  };

  useEffect(() => {
    const load = async () => {
      setLoading(false);
      const res = await updateLocation({ page: currentPage, limit: 10, search: debouncedSearch });
      setData(res?.dealersList ?? []);
      setTotalPages(res?.pagination?.totalPages ?? 1);
      setTotalCount(
        res?.pagination?.totalCount ??
          res?.pagination?.total ??
          res?.pagination?.totalItems ??
          res?.pagination?.count ??
          (res?.pagination?.totalPages ?? 1) * 10
      );
      setLoading(true);
    };
    load();
  }, [message, currentPage, debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleApprove = async (id, latitude, longitude) => {
    const data = { status: "APPROVE", latitude, longitude };
    const response = await authAxios()({
      url: `/api/v1/dealers/dealer-location/update-location?dealerId=${id}`,
      method: "POST",
      data,
    });
    setMessage(response?.data.message);
    toastMessage("Location Approved", "success");
  };

  const handleRevert = async (id) => {
    const response = await authAxios()({
      url: `/api/v1/dealers/dealer-location/update-location?dealerId=${id}`,
      method: "POST",
    });
    toastMessage("Location Rejected.", "error");
    setMessage(response?.data.message);
  };

  const calculateDistance = (coordinates) => {
    const { lat1, long1, lat2, long2 } = coordinates;
    const distance = haversine({ lat: lat1, lng: long1 }, { lat: lat2, lng: long2 });
    return isNaN(distance) ? "NA" : distance.toFixed(0);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <PageHeader
        title="Dealer Location"
        subtitle="Review and approve dealer location update requests"
      />

      <SectionCard
        title="Update Requests"
        sx={{ mb: 3 }}
        action={
          <TextField
            size="small"
            placeholder="Search SAP ID, Dealer Code"
            value={search}
            onChange={handleSearchChange}
            sx={{ width: { xs: "100%", sm: 280 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        }
        contentSx={{ p: 0 }}
      >
        {loading ? (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">SO ID</TableCell>
                    <TableCell align="left">Dealer ID</TableCell>
                    <TableCell align="center">Updated On</TableCell>
                    <TableCell align="center">Existing Location</TableCell>
                    <TableCell align="center">Updated Location</TableCell>
                    <TableCell align="center">Distance (mtr)</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data && data.length > 0 ? (
                    data.map((d) => (
                      <TableRow key={d._id}>
                        <TableCell align="left">SO{d?.territoryCode}</TableCell>
                        <TableCell align="left">
                          <div className="tooltip">
                            {d?.dealerCode}
                            <span className="tooltiptext">{d?.name}</span>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {d?.locUpdatedOn ? moment(d?.locUpdatedOn).format("DD-MM-YYYY") : "-"}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            className="tooltip"
                            sx={{ cursor: "pointer", display: "flex", flexDirection: "column" }}
                            onClick={() => {
                              setOpen(true);
                              setLat(d?.latitude ?? "NA");
                              setLong(d?.longitude ?? "NA");
                            }}
                          >
                            <span>lat : {d?.latitude ?? "NA"}</span>
                            <span>long : {d?.longitude ?? "NA"}</span>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{ cursor: "pointer", display: "flex", flexDirection: "column" }}
                            onClick={() => {
                              setOpen(true);
                              setLat(d?.updatedLatitude ?? "NA");
                              setLong(d?.updatedLongitude ?? "NA");
                            }}
                          >
                            <span>lat : {d?.updatedLatitude ?? "NA"}</span>
                            <span>long : {d?.updatedLongitude ?? "NA"}</span>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {calculateDistance({
                            lat1: d?.updatedLatitude ?? 0,
                            long1: d?.updatedLongitude ?? 0,
                            lat2: d?.latitude ?? 0,
                            long2: d?.longitude ?? 0,
                          })}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="center"
                          >
                            {d.updateLocationRequest ? (
                              <Button
                                size="small"
                                onClick={() => handleApprove(d?._id, d?.updatedLatitude, d?.updatedLongitude)}
                                variant="contained"
                              >
                                Approve
                              </Button>
                            ) : (
                              "-"
                            )}
                            {d?.updateLocationRequest ? (
                              <Button
                                size="small"
                                onClick={() => handleRevert(d?._id)}
                                variant="contained"
                                color="error"
                              >
                                Revert
                              </Button>
                            ) : (
                              "-"
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ border: 0 }}>
                        <EmptyState
                          icon={LocationOffOutlinedIcon}
                          title="No Update Request Available"
                          description="Dealer location update requests will appear here once submitted."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePager
                count={totalCount}
                totalPages={totalPages}
                page={currentPage}
                rowsPerPage={10}
                onPageChange={(p) => setCurrentPage(p)}
              />
            </TableContainer>
          </>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
      </SectionCard>

      <Modal open={open} onClose={handleClose}>
        <Box sx={messageStyle}>
          <iframe
            title="Dealer location map"
            src={`https://maps.google.com/maps?q=${lat},${long}&hl=es;&output=embed`}
            height="500px"
            width="100%"
          ></iframe>
        </Box>
      </Modal>
    </>
  );
};

const UpdateLoaction = () => {
  return (
    <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default UpdateLoaction;
